import React, { useMemo, useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericSearchbar, { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";
import {
  buildReflectorColumns,
  Reflector as TableReflector,
} from "./ReflectorData";
import ReleaseLicenseModal, { refusalMessage } from "./ReleaseLicenseModal";
import ClusterPage from "./Cluster/ClusterPage";
import {
  getReflectorsList,
  releaseReflectorLicense,
  type ReleaseLicenseCode,
} from "./request/request";
import { useAuth0 } from "@auth0/auth0-react";
import { filterReflectors } from "@shared/components/utils/searchUtils";
import { mapAvailabilityToStatus } from "../../helpers/formattersShapedData";
import { useAppSelector } from "@shared/redux/hooks";
import { DataStateView } from "kontrolEdge/components/Backbone/BackboneDetails/StateViews";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { ReflectorManagementOnboardingEmptyState } from "@shared/components/EmptyItems";
import { getTableBodyMaxHeight, REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS } from "../../helpers/tableViewport";

// Using centralized mapAvailabilityToStatus from formattersShapedData

const suggestedKeys = [
  "Reflector ID",
  "Name",
  "Status",
  "Cloud",
  "Region",
  "Cluster",
  "Version",
  "Last Sync",
  "UUID",
];

// Interface for API response
interface Reflector {
  id: number;
  name: string;
  description: string;
  available: number;
  availability: number | null;
  lastUpdate: string;
  licenseId: number;
  version: string;
  data: {
    ha: string;
    cni: Record<string, string>;
    cloud: string;
    ha_ip: string | null;
    region: string;
  };
  tenantId: number;
  subId: string;
  clusterId: number | null;
  localPref: number;
  lastUpdatePeer: string;
  statusPeer: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  cluster: {
    id: number;
    name: string;
    description: string;
    enable: boolean;
  } | null;
  tenant: {
    id: number;
    Name: string;
    nameDisplay: string;
  };
}

const ReflectorsPage: React.FC = () => {
  const location = useLocation();
  const isDeviceView = location.pathname === "/reflector-v2/management/bgp-reflectors";
  const isClusterView = location.pathname === "/reflector-v2/management/clusters";

	const { tenantId } = useAppSelector(state => state.selection);
  const { isAuthenticated, isLoading: isAuthLoading, getAccessTokenSilently } = useAuth0();
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  getAccessTokenSilentlyRef.current = getAccessTokenSilently;

  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // API state
  const [reflectors, setReflectors] = useState<TableReflector[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Liberacion de licencia (CTX-357). El reflector elegido es tambien la señal
  // de que el modal esta abierto: dos estados para una sola cosa se desincronizan.
  const [releaseTarget, setReleaseTarget] = useState<TableReflector | null>(null);
  const [isReleasing, setIsReleasing] = useState(false);
  const [releaseRefusal, setReleaseRefusal] = useState<string | null>(null);

  const fetchReflectors = React.useCallback(async () => {
    if (isAuthLoading || !isAuthenticated || !tenantId) return;
    setIsFetching(true);
    setError(null);
    try {
      const token = await getAccessTokenSilentlyRef.current();
      const response = await getReflectorsList(token, tenantId);
      const shapedData: TableReflector[] = response.data.map((item: Reflector) => ({
        id: item.id ? String(item.id) : "",
        name: item.name || "",
        subId: item.subId || "",
        status: mapAvailabilityToStatus(item.available),
        cloud: item.data?.cloud ? item.data.cloud.toUpperCase() : "",
        region: item.data?.region || "",
        cluster: item.cluster?.name || (item.clusterId ? String(item.clusterId) : "—"),
        version: item.version || "-",
        lastSync: item.lastUpdate || "-",
        description: item.description || "",
        // Estado de licencia por fila (CTX-355). `hasLicense` mira el id y no
        // el serial: PAYG tambien trae serial -- el del token -- y ese no se
        // libera.
        licenseSerial: item.license?.serial ?? null,
        licenseValid: item.licenseValid,
        licenseReason: item.licenseReason,
        payg: item.payg,
        hasLicense: Boolean(item.license?.id),
      }));
      setReflectors(shapedData);
      setRowsPerPage(10);
    } catch (err) {
      console.error("Error fetching reflectors:", err);
      setError("Failed to load reflectors. Please try again.");
    } finally {
      setIsFetching(false);
    }
  }, [isAuthenticated, isAuthLoading, tenantId]);

  useEffect(() => {
    fetchReflectors();
  }, [fetchReflectors]);

  const closeReleaseModal = React.useCallback(() => {
    setReleaseTarget(null);
    setReleaseRefusal(null);
  }, []);

  /**
   * El backend decide si la liberacion procede. Un rechazo deja el modal
   * abierto con el motivo -- el del cluster es el que el usuario necesita leer
   * para saber que hacer -- y "ya estaba liberado" llega con 200, asi que se
   * trata como exito y no como error.
   */
  const confirmRelease = React.useCallback(async () => {
    if (!releaseTarget || !tenantId) return;

    setIsReleasing(true);
    setReleaseRefusal(null);
    try {
      const token = await getAccessTokenSilentlyRef.current();
      await releaseReflectorLicense(token, releaseTarget.id, tenantId);
      closeReleaseModal();
      await fetchReflectors();
    } catch (err) {
      const code = (
        err as { response?: { data?: { code?: ReleaseLicenseCode } } }
      ).response?.data?.code;
      setReleaseRefusal(refusalMessage(code));
    } finally {
      setIsReleasing(false);
    }
  }, [releaseTarget, tenantId, closeReleaseModal, fetchReflectors]);

  const columns = useMemo(
    () => buildReflectorColumns({ onReleaseLicense: setReleaseTarget }),
    []
  );


  // Filtering logic using utility function
  const filteredData = useMemo(() => {
    return filterReflectors(reflectors, activeFilters);
  }, [activeFilters, reflectors]);

  return (
    <div className="relative">
      <GenericCard isCollapsible={false}>
        {/* Device View */}
        {isDeviceView && (
          <>
            <div className="mb-2 p-4">
              <GenericSearchbar
                placeholder="Find by attribute or value (case-sensitive)"
                suggestedKeys={suggestedKeys}
                onFiltersChange={setActiveFilters}
              />
            </div>
            <DataStateView
              loading={isFetching}
              error={error}
              isEmpty={!isFetching && !error && reflectors.length === 0}
              onRetry={fetchReflectors}
              emptyContent={
                <ReflectorManagementOnboardingEmptyState heading="You have no Reflectors yet" />
              }
              loadingContent={
                <TableSkeleton
                  columns={columns.length}
                  rows={8}
                  className="mx-4 mb-4"
                />
              }
            >
              <GenericTable
                columns={columns}
                data={filteredData}
                initialRowsPerPage={rowsPerPage}
                rowsPerPageOptions={[...REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
                maxTableBodyHeight={getTableBodyMaxHeight(280)}
              />
            </DataStateView>
          </>
        )}
        {/* Cluster View */}
        {isClusterView && <ClusterPage />}
      </GenericCard>

      <ReleaseLicenseModal
        isOpen={releaseTarget !== null}
        reflectorName={releaseTarget?.name ?? ""}
        licenseSerial={releaseTarget?.licenseSerial ?? undefined}
        isReleasing={isReleasing}
        refusal={releaseRefusal}
        onConfirm={confirmRelease}
        onClose={closeReleaseModal}
      />

    </div>
  );
};

export default ReflectorsPage; 